import { useContext, useEffect, useState, useRef, useCallback } from "react";
//Components
import MultiDropDownObjects from "./MultiDropDownObjects";
import {
  TextField,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import PaginatedTableWrapper from "./PaginatedTableWrapper";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import SearchModal from "./SearchModal";
import SearchButtons from "./SearchButtons";
import HelpIconButton from "./HelpIconButton";
//Fetching
import { useLazyQuery } from "@apollo/client";
import { GET_ORIS_QUERY } from "../Queries/GetOrisQuery";
import { SPECIFIC_SETUP_LISTS_QUERY } from "../Queries/SetupListsQuery";
import { ALL_OFFICERS_QUERY } from "../Queries/AllOfficersQuery";
import { INCIDENT_SEARCH_QUERY } from "../Queries/IncidentSearchQuery";
//Contexts
import { UserContext } from "../Contexts/UserContext";
//styling
import { withStyles } from "@material-ui/core/styles";
import { useStyles } from "../Styles/styles";
//utils
import {
  localStoreData,
  parseObjectArrayToIdArray,
  formatOfficerName,
  checkLocalStorageAge,
  localGetData,
  parseDateFlexible,
} from "../utils/utils";
import {
  parseIncidentSearch,
  incidentSearchHeaders,
} from "../utils/SearchHelpers";
import WrappedKeyboardDateTimePicker from "./WrappedKeyboardDateTimePicker";

import moment from "moment";

const initialSearchState = {
  ori_id: null,
  classification_id: null,
  incident_number: "",
  created_at_search_start_date: moment()
    .local()
    .subtract(1, "years")
    .format("MM/DD/YYYY HH:mm:ss"),
  created_at_search_end_date: moment().format("MM/DD/YYYY HH:mm:ss"),
  address_attributes: {
    street_number: "",
    street_name: "",
    city: "",
  },
  team_ids: null,
  officer_incident_specifics_attributes: {
    0: {
      person_id: null,
    },
  },
};
const IncidentSearch = ({
  showModal,
  setShowModal,
  handleIncidentOpening,
  setPausePolling,
}) => {
  const classes = useStyles();
  const { user, responseErrorHandler, nightMode, onlineState } =
    useContext(UserContext);

  const inputLabel = useRef(null);

  const [localSpecificSetup, setLocalSpecificSetup] = useState(null);

  const [selectedOri, setSelectedOri] = useState(user?.homeOriId?.toString());
  const [oriList, setOriList] = useState([]);
  const [officers, setOfficers] = useState([]);

  const [resultsPerPage, setResultsPerPage] = useState(25);
  const [searchResults, setSearchResults] = useState(null);

  const [searchState, setSearchState] = useState(initialSearchState);

  const { setupLists: specificSetupLists } = localSpecificSetup || {};
  const { classifications, teams } = specificSetupLists || {};

  const [getSpecificSetup] = useLazyQuery(SPECIFIC_SETUP_LISTS_QUERY, {
    context: { serializationKey: "incidentQueue" },
    fetchPolicy: { requestPolicy: "no-cache" },
    variables: { originatorId: user?.id },
    onError: error => {
      console.error(JSON.stringify(error));
    },
    onCompleted: data => {
      if (data) {
        responseErrorHandler(data);
        localStoreData(data, `specific-setup-${searchState.ori_id}`);
        checkForSpecificSetup(searchState.ori_id);
      }
    },
  });

  const [
    searchIncidents,
    { loading: incidentSearchLoading, error: incidentSearchError },
  ] = useLazyQuery(INCIDENT_SEARCH_QUERY, {
    context: { serializationKey: "incidentQueue" },
    variables: { originatorId: user?.id },
    fetchPolicy: "no-cache",
    onError: error => {
      console.error(JSON.stringify(error));
    },
    onCompleted: data => {
      if (data) {
        responseErrorHandler(data);
        setSearchResults(data?.searchIncidents);
        setShowSearchOptions(false);
      }
    },
  });

  const [getOfficers] = useLazyQuery(ALL_OFFICERS_QUERY, {
    context: { serializationKey: "incidentQueue" },
    onError: error => {
      console.error(JSON.stringify(error));
    },
    onCompleted: data => {
      if (data) {
        responseErrorHandler(data);
        localStoreData(data, `mp-${selectedOri}-officers`);
        checkForOfficers();
      }
    },
  });

  useEffect(() => {
    if (showModal && searchState.ori_id && searchState.ori_id !== "") {
      setSelectedOri(searchState.ori_id);
    }
  }, [searchState.ori_id, showModal]);

  const checkForOfficers = useCallback(() => {
    if (localGetData(`mp-${selectedOri}-officers`)) {
      // console.log('Found the officers in local storage')
      const officers = localGetData(`mp-${selectedOri}-officers`);
      const now = moment();
      const age = now.diff(officers.timestamp, "hours");
      if (age > 8 && onlineState) {
        // console.log('Officers were stale')
        localStorage.removeItem(`mp-${selectedOri}-officers`);
        getOfficers({ variables: { oriIds: selectedOri } });
      } else {
        setOfficers(localGetData(`mp-${selectedOri}-officers`)?.allOfficers);
      }
    } else {
      // console.log('Fetching the officers')
      getOfficers({ variables: { oriIds: selectedOri } });
    }
  }, [getOfficers, onlineState, selectedOri]);

  useEffect(() => {
    if (showModal && selectedOri && selectedOri !== "") {
      checkForOfficers();
    }
  }, [checkForOfficers, selectedOri, showModal]);

  const checkForSpecificSetup = useCallback(
    oriId => {
      if (!oriId) {
        return;
      } else {
        // console.log('Checking for specific setup')

        if (localGetData(`specific-setup-${oriId}`)) {
          // console.log('Found the specific setup in local storage')
          const setup = localGetData(`specific-setup-${oriId}`);
          const now = moment();
          const age = now.diff(setup.timestamp, "hours");
          if (age > 8 && onlineState) {
            // console.log('Setup item was stale')
            localStorage.removeItem(`specific-setup-${oriId}`);
            getSpecificSetup({ variables: { oriId: oriId } });
          } else {
            setLocalSpecificSetup(localGetData(`specific-setup-${oriId}`));
          }
        } else {
          // console.log('Fetching the specific setup')
          getSpecificSetup({ variables: { oriId: oriId } });
        }
      }
    },
    [getSpecificSetup, onlineState],
  );

  const [getOris, { loading: orisLoading }] = useLazyQuery(GET_ORIS_QUERY, {
    context: { serializationKey: "incidentQueue" },
    fetchPolicy: "no-cache",
    onError: error => {
      console.error(JSON.stringify(error));
    },
    onCompleted: data => {
      if (data) {
        responseErrorHandler(data);
        setOriList(data.getOris);
        localStoreData(data, `mp-oris`);
      }
    },
  });

  useEffect(() => {
    if (checkLocalStorageAge(`mp-oris`, 8)) {
      setOriList(localGetData(`mp-oris`)?.getOris);
    } else {
      !orisLoading && getOris();
    }
    setSearchState(prevState => {
      return { ...prevState, ori_id: user?.homeOriId?.toString() };
    });
  }, [getOris, orisLoading, user?.homeOriId]);

  useEffect(() => {
    showModal && checkForSpecificSetup(searchState.ori_id);
  }, [showModal, searchState.ori_id, checkForSpecificSetup]);

  const handleChange = (value, name) => {
    switch (name) {
      case "ori_id":
        setSearchState(prevState => {
          return {
            ...prevState,
            [name]: value?.id,
            classification_id: null,
            team_ids: null,
          };
        });
        return null;

      case "classification_id":
        setSearchState(prevState => {
          return { ...prevState, [name]: parseObjectArrayToIdArray(value) };
        });
        return null;
      case "incident_number":
        setSearchState(prevState => {
          return { ...prevState, [name]: value };
        });
        return null;
      case "team_ids":
        setSearchState(prevState => {
          return { ...prevState, [name]: parseObjectArrayToIdArray(value) };
        });
        return null;
      case "resultsPerPage":
        setResultsPerPage(value);
        return null;
      default:
        alert(`Can't find ${name} in handleChange`);
        return null;
    }
  };

  useEffect(() => {
    if (searchResults?.length < 0) {
      setSearchResults([]);
    }
  }, [searchState, resultsPerPage, searchResults?.length]);

  const handleDateChange = (strDate, name, dateObj) => {
    setSearchState(prevState => {
      return { ...prevState, [name]: dateObj };
    });
    return null;
  };

  const StyledAccordionSummary = withStyles({
    root: {
      backgroundColor: props =>
        props.nightmode === "true" ? "#616161" : "#fae8ad",
      // backgroundColor: '#fae8ad',
      borderBottom: "1px solid rgba(0, 0, 0, .125)",
      marginBottom: -1,
      minHeight: 56,
      "&$expanded": {
        minHeight: 56,
      },
    },
    content: {
      "&$expanded": {
        // margin: '12px 0',
        margin: "0",
      },
    },
    expanded: {},
  })(AccordionSummary);

  const handleAddressChange = (value, name) => {
    let localAddress = JSON.parse(
      JSON.stringify(searchState.address_attributes),
    );
    localAddress[name] = value;
    setSearchState(prevState => {
      return { ...prevState, address_attributes: localAddress };
    });
  };

  const handleOfficerChange = (value) => {
    setSearchState(prevState => {
      return {
        ...prevState,
        officer_incident_specifics_attributes: [
          { person_id: parseObjectArrayToIdArray(value) },
        ],
      };
    });
  };

  const submitSearch = (_perPage = resultsPerPage, page = 1) => {
    const searchStr = JSON.stringify(searchState);
    searchIncidents({
      variables: { perPage: resultsPerPage, page: page, json: searchStr },
    });
  };

  const dashRef = useRef(null);

  const [dashSize, setDashSize] = useState({
    width: dashRef?.current?.offsetWidth,
    height: undefined,
  });

  useEffect(() => {
    function handleResize() {
      dashRef.current &&
        setDashSize({
          width: dashRef.current.offsetWidth,
          height: dashRef.current.offsetHeight,
        });
    }
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    setDashSize({
      width: dashRef?.current?.offsetWidth,
      height: dashRef?.current?.offsetHeight,
    });
  }, [dashRef, showModal]);

  const [showSearchOptions, setShowSearchOptions] = useState(true);

  return showModal ? (
    <SearchModal showModal={showModal} setShowModal={setShowModal}>
      <form
        className={classes.paper}
        ref={dashRef}
        style={{ display: "flex", flexDirection: "column", padding: 0 }}
      >
        {/* <div id="search-header" style={{ backgroundColor: '#fae8ad', padding: '1vw' }}>
          <h3 className={classes.tableH3Title}>Incident Search</h3>
        </div> */}
        <Accordion
          nightMode={nightMode}
          square
          expanded={showSearchOptions}
          onChange={() => setShowSearchOptions(!showSearchOptions)}
          TransitionProps={{ unmountOnExit: false }}
        >
          <StyledAccordionSummary
            nightmode={nightMode?.toString()}
            style={{ margin: 0 }}
            aria-controls="panel1d-content"
            id="panel1d-header"
          >
            <Box flexGrow="1" display="flex" justifyContent="space-between">
              <Typography
                style={{
                  display: "flex",
                  alignItems: "center",
                  color: nightMode ? "white" : "#303e4f",
                }}
                className={classes.expansionPanelTypography}
                data-cy="initial-call-information-panel-header"
              >
                Incident Search{" "}
                {showSearchOptions ? <ExpandMoreIcon /> : <ExpandLessIcon />}
              </Typography>
              <HelpIconButton objectname="mobile_incident_search" />
            </Box>
          </StyledAccordionSummary>
          <AccordionDetails type="button" onClick={e => e.stopPropagation()}>
            <div
              style={{ display: "flex", flexDirection: "column", padding: 0 }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  flexWrap: "wrap",
                  height: "auto",
                  justifyContent: "flex-start",
                }}
              >
                <Autocomplete
                  autoSelect
                  value={
                    oriList?.find(ori => ori?.id === searchState.ori_id) || ""
                  }
                  id="incident-search-oriList-autocomplete"
                  options={oriList}
                  getOptionLabel={option => {
                    if (option.oriName && option.abbreviation) {
                      return option?.oriName + " - " + option?.abbreviation;
                    } else if (option.oriName || option.abbreviation) {
                      return option.oriName || option.abbreviation;
                    } else {
                      return "";
                    }
                  }}
                  style={{ width: 300 }}
                  renderInput={params => (
                    <TextField
                      {...params}
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        style: { borderRadius: "5px" },
                        ...params.InputProps,
                      }}
                      label="ORI"
                    />
                  )}
                  onChange={(e, value) => handleChange(value, "ori_id")}
                  autoHighlight
                  className={classes.matchMargin}
                />
                <MultiDropDownObjects
                  id={"selected-call-types"}
                  data-cy={"selected-call-types"}
                  title={"Call Types"}
                  stateName={"classification_id"}
                  handleChange={e =>
                    handleChange(e.target.value, "classification_id")
                  }
                  selectedValues={
                    searchState?.classification_id && classifications
                      ? classifications.filter(option =>
                          searchState?.classification_id.some(
                            classId => option.id === classId,
                          ),
                        )
                      : []
                  }
                  options={classifications}
                  disabled={searchState.ori_id ? false : true}
                  helperText={
                    searchState.ori_id ? null : "Please select an ORI"
                  }
                  helperTextStyle={classes.redLabel}
                  formStyle={classes.formControl}
                  labelStyle={classes.multiInputLabel}
                  selectStyle={null}
                  chipsStyle={classes.chips}
                  chipStyle={classes.chip}
                  // bgColor={'white'}
                />
                <TextField
                  data-cy="incident-number"
                  name={"incident_number"}
                  inputProps={{ "data-cy": "incident-number-input" }}
                  InputProps={{ style: { borderRadius: "5px" } }}
                  InputLabelProps={{ shrink: true }}
                  id="incident-number-id"
                  label="Incident Number"
                  value={searchState.incident_number}
                  className={classes.matchMargin}
                  onChange={e =>
                    handleChange(e.target.value, "incident_number")
                  }
                />
                <FormControl variant="standard" className={classes.formControl}>
                  <WrappedKeyboardDateTimePicker
                    ampm={false}
                    className={classes.smallMatchMargin}
                    clearable={true}
                    data-cy="start-date"
                    disabled={false}
                    disableFuture={true}
                    format="MM/DD/YYYY HH:mm:ss"
                    InputLabelProps={{ shrink: true }}
                    InputProps={{ style: { borderRadius: "5px" } }}
                    label="Start Date"
                    maxDateMessage={"Date cannot be in the future"}
                    name="created_at_search_start_date"
                    onBlur={() => setPausePolling(false)}
                    onChange={handleDateChange}
                    onFocus={() => setPausePolling(true)}
                    showTodayButton={true}
                    value={parseDateFlexible(
                      searchState.created_at_search_start_date,
                    )}
                    // style={{ width: "100%" }}
                  />
                </FormControl>
                <FormControl variant="standard" className={classes.formControl}>
                  <WrappedKeyboardDateTimePicker
                    ampm={false}
                    className={classes.smallMatchMargin}
                    clearable={true}
                    dataCy="end-date"
                    disabled={false}
                    disableFuture={true}
                    format="MM/DD/YYYY HH:mm:ss"
                    InputLabelProps={{ shrink: true }}
                    InputProps={{ style: { borderRadius: "5px" } }}
                    label="End Date"
                    maxDateMessage={"Date cannot be in the future"}
                    name="created_at_search_end_date"
                    onBlur={() => setPausePolling(false)}
                    onChange={handleDateChange}
                    onFocus={() => setPausePolling(true)}
                    showTodayButton={true}
                    value={parseDateFlexible(
                      searchState.created_at_search_end_date,
                    )}
                    // style={{ width: "100%" }}
                  />
                </FormControl>
                <TextField
                  data-cy="street-number"
                  name={"street_number"}
                  inputProps={{ "data-cy": "street-number-input" }}
                  InputProps={{ style: { borderRadius: "5px" } }}
                  InputLabelProps={{ shrink: true }}
                  id="street-number-id"
                  label="Street Number"
                  value={searchState.address_attributes.street_number}
                  className={classes.matchMargin}
                  onChange={e =>
                    handleAddressChange(e.target.value, "street_number")
                  }
                />
                <TextField
                  data-cy="street-name"
                  name={"street_name"}
                  inputProps={{ "data-cy": "street-name-input" }}
                  InputProps={{ style: { borderRadius: "5px" } }}
                  InputLabelProps={{ shrink: true }}
                  id="street-name-id"
                  label="Street Name"
                  value={searchState.address_attributes.street_name}
                  className={classes.matchMargin}
                  onChange={e =>
                    handleAddressChange(e.target.value, "street_name")
                  }
                />
                <TextField
                  data-cy="city"
                  name={"city"}
                  inputProps={{ "data-cy": "city-input" }}
                  InputProps={{ style: { borderRadius: "5px" } }}
                  InputLabelProps={{ shrink: true }}
                  id="city-id"
                  label="City"
                  value={searchState.address_attributes.city}
                  className={classes.matchMargin}
                  onChange={e => handleAddressChange(e.target.value, "city")}
                />
                {/* <MultiDropDownObjects
            id={"selected-teams"}
            data-cy={"selected-teams"}
            title={"Teams"}
            stateName={"team_ids"}
            handleChange={(e) => handleChange(e.target.value, "team_ids")}
            selectedValues={searchState?.team_ids && teams ? teams.filter(option => searchState?.team_ids.some(teamId => option.id === teamId)) : []}
            options={teams}
            disabled={searchState.ori_id ? false : true}
            helperText={searchState.ori_id ? null : "Please select an ORI"}
            helperTextStyle={classes.redLabel}
            formStyle={classes.formControl}
            labelStyle={classes.multiInputLabel}
            selectStyle={null}
            chipsStyle={classes.chips}
            chipStyle={classes.chip}
            bgColor={'white'}
          /> */}
                <div className={classes.matchMargin}>
                  <Autocomplete
                    autoSelect
                    value={
                      teams?.filter(
                        el => searchState.team_ids?.indexOf(el.id) >= 0,
                      ) || ""
                    }
                    disabled={searchState.ori_id ? false : true}
                    multiple={true}
                    id="incident-search-teams-autocomplete"
                    options={teams || []}
                    getOptionLabel={option => option?.name || ""}
                    style={{ width: 300 }}
                    renderInput={params => (
                      <TextField
                        {...params}
                        InputLabelProps={{ shrink: true }}
                        InputProps={{
                          style: { borderRadius: "5px" },
                          ...params.InputProps,
                        }}
                        label="Teams"
                      />
                    )}
                    onChange={(e, value) => handleChange(value, "team_ids")}
                    autoHighlight
                  />
                  {!searchState.ori_id && (
                    <FormHelperText className={classes.redLabel}>
                      Please select an ORI
                    </FormHelperText>
                  )}
                </div>
                <Autocomplete
                  autoSelect
                  multiple={true}
                  value={
                    officers?.filter(
                      el =>
                        searchState.officer_incident_specifics_attributes[0]?.person_id?.indexOf(
                          el.id,
                        ) >= 0,
                    ) || ""
                  }
                  id="incident-search-officers-autocomplete"
                  options={officers}
                  getOptionLabel={option => formatOfficerName(option)}
                  style={{ width: 300 }}
                  renderInput={params => (
                    <TextField
                      {...params}
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        style: { borderRadius: "5px" },
                        ...params.InputProps,
                      }}
                      label="Officers"
                    />
                  )}
                  onChange={(e, value) =>
                    handleOfficerChange(value, "person_id")
                  }
                  autoHighlight
                  className={classes.matchMargin}
                />
                <div className={classes.matchMargin}>
                  <InputLabel
                    shrink={true}
                    className={classes.label}
                    data-cy={"results-per-page-label"}
                    ref={inputLabel}
                    id={"results-per-page-label"}
                  >
                    Results Per Page
                  </InputLabel>
                  <Select
                    data-cy={"results-per-page-select"}
                    id={"results-per-page-select"}
                    name={"resultPerPage"}
                    value={resultsPerPage}
                    native={false}
                    // renderValue={() => !nameFormatter ? (value?.[name] ? value[name] : " ") : nameFormatter(value)}
                    onChange={e =>
                      handleChange(e.target.value, "resultsPerPage")
                    }
                    style={{ marginTop: "0px", borderRadius: "5px" }}
                  >
                    <MenuItem
                      data-cy={"10-results-per-page"}
                      key={10}
                      value={10}
                    >
                      10
                    </MenuItem>
                    <MenuItem
                      data-cy={"25-results-per-page"}
                      key={25}
                      value={25}
                    >
                      25
                    </MenuItem>
                    <MenuItem
                      data-cy={"50-results-per-page"}
                      key={50}
                      value={50}
                    >
                      50
                    </MenuItem>
                  </Select>
                </div>
              </div>
              <SearchButtons
                loading={incidentSearchLoading}
                submitSearch={submitSearch}
                setSearchResults={setSearchResults}
                setShowModal={setShowModal}
                resetSearchState={() => setSearchState(initialSearchState)}
                clearSearchResults={() => setSearchResults(null)}
              />
            </div>
          </AccordionDetails>
        </Accordion>
        {!!searchResults && (
          <PaginatedTableWrapper
            dashSize={dashSize}
            tableHeight={dashRef?.current?.offsetHeight * 0.95}
            tableName={"Incident Search Results"}
            headers={incidentSearchHeaders}
            userId={user?.id}
            paginatedQueryHandler={submitSearch}
            rows={
              searchResults
                ? parseIncidentSearch(
                    searchResults,
                    handleIncidentOpening,
                    setShowModal,
                    user?.roleNames,
                  )
                : []
            }
            loading={incidentSearchLoading}
            error={incidentSearchError}
            paginated={true}
            rowsPerPage={resultsPerPage}
          />
        )}
      </form>
    </SearchModal>
  ) : null;
};

// ORI
// Call Type
// Incident Number
// Start Date
// End Date
// Street#
// Street Name
// City
// Team
// Officer

export default IncidentSearch;
